/**
 * SubsInviteModal — invite flow from the global Subs & Vendors screen.
 * Picks a project first, then reuses InviteSubModal for the actual invite.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchProjects } from '../../api/projects'
import InviteSubModal from '../projects/modal/InviteSubModal'
import ModalPortal, { modalBoxStyle } from '../../components/ModalPortal'

interface Props {
  onClose: () => void
}

export default function SubsInviteModal({ onClose }: Props) {
  const [projectId, setProjectId] = useState<number | null>(null)

  const { data: projects = [], isPending } = useQuery({
    queryKey: ['projects'],
    queryFn:  fetchProjects,
    staleTime: 60_000,
  })

  if (projectId != null) {
    return (
      <InviteSubModal
        projectId={projectId}
        onClose={onClose}
      />
    )
  }

  return (
    <ModalPortal testId="subs-invite-modal-backdrop" onBackdropClick={onClose}>
      <div className="panel" style={{ ...modalBoxStyle, width: 420, maxWidth: '94vw' }} data-testid="subs-invite-modal">
        <div className="panel-h">
          <div className="panel-tt">Invite sub to project</div>
          <button type="button" className="pm-x" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div style={{ padding: '16px 18px' }}>
          <p style={{ fontSize: 12, color: 'var(--ink3)', margin: '0 0 14px', lineHeight: 1.5 }}>
            Choose which project to invite this sub to. They will receive a magic link and project email alias.
          </p>
          <label className="fld-l" htmlFor="subs-invite-project">Project</label>
          <select
            id="subs-invite-project"
            className="fld-i"
            style={{ width: '100%', marginBottom: 16 }}
            value=""
            disabled={isPending || projects.length === 0}
            onChange={e => {
              const id = parseInt(e.target.value, 10)
              if (!Number.isNaN(id)) setProjectId(id)
            }}
            data-testid="subs-invite-project-select"
          >
            <option value="">
              {isPending ? 'Loading projects…' : projects.length === 0 ? 'No projects yet' : 'Select a project…'}
            </option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>
                {p.name ?? `Project #${p.id}`}
              </option>
            ))}
          </select>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" className="btn b-gh btn-sm" onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    </ModalPortal>
  )
}
